import { Haptics, ImpactStyle } from '@capacitor/haptics';
import type { PopupElement } from 'inclusive-elements';

import { isTouchPress } from '../utils/events.ts';

/**
 * Make popups behave like menus on iOS.
 *
 * Open popups go in the top layer, so a parent hiding its overflow can't cut
 * them off. A touch outside closes them at once, dragging on a menu doesn't
 * scroll the page behind it, and a menu's button can be held to choose from
 * it.
 */
export function enablePopups() {
    // The open event doesn't bubble, so it's listened for in the capture phase.
    document.addEventListener(
        'open',
        (event) => {
            const popup = event.target as PopupElement;
            if (popup.localName !== 'ui-popup') return;

            // On iOS, dragging on a menu scrolls the page behind it, even with
            // its overflow hidden. A scrolling menu can keep the drag with
            // overscroll-behavior, but one that doesn't scroll can't, so it
            // blocks dragging. Measured before the move below, so layout runs
            // once.
            const menu = popup.querySelector<HTMLElement>(':scope > .Menu');
            if (menu) {
                menu.style.touchAction = menu.scrollHeight > menu.clientHeight ? 'pan-y' : 'none';
            }

            // The backdrop goes first, so it covers the screen under the
            // content. Moving the popup in the page takes both out of the top
            // layer, so this runs on every open.
            showInTopLayer(popup.shadowRoot?.querySelector<HTMLElement>('[part=backdrop]'));
            showInTopLayer(popup.children[1] as HTMLElement | undefined);
        },
        { capture: true },
    );

    // On touch screens, a menu's backdrop lets touches through (see
    // _base.less), so a touch outside reaches the page and a drag there
    // scrolls it. The touch closes the menu here instead, and if it's a tap,
    // its click is swallowed, so a tap outside only closes. A drawer's backdrop
    // still catches touches, which then target the popup.
    //
    // Lists appended to the body, like a select's in a menu, count as inside.
    document.addEventListener(
        'pointerdown',
        (event) => {
            if (!isTouchPress(event)) return;

            const target = event.target as Element;
            const inList = !!target.closest?.('[popover]');
            const open = document.querySelectorAll<PopupElement>('ui-popup[open]');
            const closing = [...open].filter(
                (popup) => popup === target || (!popup.contains(target) && !inList),
            );
            if (!closing.length) return;

            closing.forEach((popup) => (popup.open = false));

            const { pointerId } = event;
            const listening = new AbortController();
            const end = (event: PointerEvent) => {
                if (event.pointerId !== pointerId) return;
                listening.abort();
                if (event.type === 'pointerup') suppressNextClick();
            };
            addEventListener('pointerup', end, { signal: listening.signal });
            addEventListener('pointercancel', end, { signal: listening.signal });
        },
        { passive: true },
    );

    document.addEventListener('pointerdown', holdToChoose, { passive: true });
}

// How long a touch is held before the menu opens, and how far it can move
// before then and still open it.
const HOLD_DELAY = 300;
const HOLD_SLOP = 10;

/**
 * Let a touch held on a menu's button open the menu, then slide to an item and
 * lift to choose it, as on iOS.
 *
 * A quick tap still opens the menu as a click. Only buttons with
 * `touch-action: none` can be held, as iOS can scroll the page from others
 * before the page can stop it, so the stylesheet decides (see _base.less).
 */
function holdToChoose(event: PointerEvent) {
    const popup = (event.target as Element).closest?.<PopupElement>('ui-popup');
    const [button, menu] = popup?.children ?? [];
    if (
        !isTouchPress(event) ||
        !popup ||
        popup.open ||
        popup.disabled ||
        !button?.contains(event.target as Node) ||
        getComputedStyle(button).touchAction !== 'none'
    ) {
        return;
    }

    const { pointerId, clientX, clientY } = event;
    const gesture = new AbortController();
    const { signal } = gesture;
    let item: Element | undefined;
    let choosing = false;

    // Clicking the button, rather than just opening the menu, runs its own
    // handlers too, such as saving a bookmark before its menu offers
    // collections.
    const hold = setTimeout(() => {
        choosing = true;
        (button as HTMLElement).click();
        Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
        Haptics.selectionStart().catch(() => {});
    }, HOLD_DELAY);

    signal.addEventListener('abort', () => {
        clearTimeout(hold);
        item?.classList.remove('is-pressed');
        if (choosing) Haptics.selectionEnd().catch(() => {});
    });

    // The item under the finger highlights as it passes, with a tick like a
    // picker wheel's.
    addEventListener(
        'pointermove',
        (event) => {
            if (event.pointerId !== pointerId) return;

            if (!choosing) {
                if (Math.hypot(event.clientX - clientX, event.clientY - clientY) > HOLD_SLOP) {
                    gesture.abort();
                }
                return;
            }

            const under = document
                .elementFromPoint(event.clientX, event.clientY)
                ?.closest('[role^=menuitem]:not(:disabled)');
            const next = under && menu.contains(under) ? under : undefined;
            if (next === item) return;

            item?.classList.remove('is-pressed');
            next?.classList.add('is-pressed');
            if (next) Haptics.selectionChanged().catch(() => {});
            item = next;
        },
        { signal },
    );

    // Lifting on an item chooses it. Lifting anywhere else leaves the menu
    // open, as on iOS. The touch began on the button, so its click is
    // swallowed, or it would close the menu again.
    const end = (event: PointerEvent) => {
        if (event.pointerId !== pointerId) return;

        if (choosing && event.type === 'pointerup') {
            (item as HTMLElement | undefined)?.click();
            suppressNextClick();
        }

        gesture.abort();
    };
    addEventListener('pointerup', end, { signal });
    addEventListener('pointercancel', end, { signal });
}

/**
 * Wait for an opening popup to finish its transition.
 *
 * A list dropping out of a menu waits for this, as the menu cuts it off while
 * it's still opening.
 */
export function popupTransitioned(popup: Element) {
    const content = popup.children[1];

    // Its animations start a few frames after it opens, so wait for
    // enter-active to come off instead.
    return new Promise<void>((resolve) => {
        if (!content?.classList.contains('enter-active')) return resolve();

        const observer = new MutationObserver(() => {
            if (content.classList.contains('enter-active')) return;
            observer.disconnect();
            resolve();
        });
        observer.observe(content, { attributeFilter: ['class'] });
    });
}

/**
 * Show an element in the top layer, above the page and any open popups.
 *
 * One already showing is shown again, to move it above popups opened since.
 * Browsers without popovers (before iOS 17) leave it in place.
 */
export function showInTopLayer(el?: HTMLElement | null) {
    if (!el || !('showPopover' in el)) return;

    el.popover = 'manual';
    if (el.matches(':popover-open')) el.hidePopover();
    el.showPopover();
}

/**
 * Swallow the click a touch is about to send after its pointerup.
 *
 * The click can come a little late, or not at all if the finger moved, so
 * it's only waited for briefly.
 */
export function suppressNextClick() {
    const waiting = new AbortController();

    // Clicks from code go through, such as the one glassStretch.ts sends for
    // a touch that moved, which tapCancelling.ts may already be waiting on.
    const suppress = (event: Event) => {
        if (!event.isTrusted) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        waiting.abort();
    };
    addEventListener('click', suppress, { capture: true, signal: waiting.signal });
    setTimeout(() => waiting.abort(), 300);
}
